// @ts-nocheck baseline 2026-02-16
import { useEffect, useState, useRef } from "react";
import { type EmitterSubscription } from "react-native";
import LegacyKitModule from "../../modules/legacy-kit/src/LegacyKitModule";
import { genericStorage } from "@src/services/app-storage";

const STORAGE_KEY_HIRED = "holidayReminderHired";
const STORAGE_KEY_REMIND_COUNT = "holidayReminderRemindCount";
const STORAGE_KEY_DISMISS_TIMESTAMP = "holidayReminderDismissTimestamp";
const STORAGE_KEY_DISMISS_COUNT = "holidayReminderDismissCount";
const MAX_REMIND_COUNT = 2;
const DISMISS_COOLDOWN_DAYS = 3;

type UserAuthenticatedEvent = {
  email?: string;
};

/**
 * Hook to listen for when user authenticates and lands on CameraListController.
 * Returns a boolean indicating whether the HolidayReminderModal should be shown.
 *
 * Persistence logic:
 * - If user hires a camera successfully, the modal will never show again
 * - If user selects "Remind me later", the modal can show up to 2 more times (3 total)
 * - After 3 times, the modal will not show again
 * - If user dismisses the modal twice, the modal will never show again
 */
export function useHolidayReminderModal(modalStateRef?: RefObject<boolean>) {
  const [showModal, setShowModal] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [hireButtonLoading, setHireButtonLoading] = useState(false);

  // Keep ref in sync with state
  useEffect(() => {
    if (modalStateRef) {
      modalStateRef.current = showModal;
    }
  }, [showModal, modalStateRef]);

  const shouldShowModal = (): boolean => {
    // Check if we're past the cut-off date (December 5th, 2025)
    const now = new Date();
    const cutoffDate = new Date(2025, 11, 5); // Month is 0-indexed, so 11 = December
    if (now > cutoffDate) {
      return false;
    }

    // Check if user has already hired a camera
    const hasHired = genericStorage.get<boolean>(STORAGE_KEY_HIRED, false) ?? false;
    if (hasHired) {
      return false;
    }

    // Check dismiss behavior (X button or overlay tap)
    const dismissCount = genericStorage.get<number>(STORAGE_KEY_DISMISS_COUNT, 0) ?? 0;
    if (dismissCount >= 2) {
      // User has dismissed twice, never show again
      return false;
    }

    if (dismissCount === 1) {
      // User dismissed once, check if 3 days have passed
      const dismissTimestamp = genericStorage.get<number>(STORAGE_KEY_DISMISS_TIMESTAMP, 0) ?? 0;
      if (dismissTimestamp > 0) {
        const daysSinceDismiss = (Date.now() - dismissTimestamp) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < DISMISS_COOLDOWN_DAYS) {
          // Still within cooldown period
          return false;
        }
      }
    }

    // Check how many times they've been reminded
    const remindCount = genericStorage.get<number>(STORAGE_KEY_REMIND_COUNT, 0) ?? 0;
    if (remindCount >= MAX_REMIND_COUNT) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    // Listen for the event on both iOS and Android
    const subscription = LegacyKitModule.addListener(
      "onUserAuthenticatedAndLandedOnCameraList",
      async (event: UserAuthenticatedEvent) => {
        console.log("received onUserAuthenticatedAndLandedOnCameraList event", event);
        setUserEmail(event?.email ?? null);

        // Only show modal if persistence rules allow it
        if (shouldShowModal()) {
          // Set modal state AND update ref synchronously before dismissing
          setShowModal(true);
          if (modalStateRef) {
            modalStateRef.current = true;
          }

          // Dismiss the legacy activity immediately
          try {
            await LegacyKitModule.dismissLegacyApp();
          } catch (error) {
            console.warn("[useHolidayReminderModal] Failed to dismiss legacy app", error);
          }
        }
      },
    ) as EmitterSubscription;

    return () => {
      subscription.remove();
    };
  }, [modalStateRef]);

  const handleHolidayModalHireCamera = async () => {
    if (!userEmail) {
      return;
    }

    setHireButtonLoading(true);

    try {
      const response = await fetch("https://hooks.zapier.com/hooks/catch/25370431/u8aql5m/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: userEmail }),
      });

      if (!response.ok) {
        // Don't persist if request failed
        return;
      }

      // Persist that user has hired a camera - modal will never show again
      genericStorage.set(STORAGE_KEY_HIRED, true);
    } catch (error) {
      // Don't persist if request failed
    } finally {
      setHireButtonLoading(false);
    }
  };

  const handleRemindLater = () => {
    // Increment the remind count
    const currentCount = genericStorage.get<number>(STORAGE_KEY_REMIND_COUNT, 0) ?? 0;
    const newCount = currentCount + 1;
    genericStorage.set(STORAGE_KEY_REMIND_COUNT, newCount);
  };

  const handleModalDismiss = () => {
    const currentDismissCount = genericStorage.get<number>(STORAGE_KEY_DISMISS_COUNT, 0) ?? 0;

    if (currentDismissCount === 0) {
      // First dismissal - store timestamp and increment count
      genericStorage.set(STORAGE_KEY_DISMISS_TIMESTAMP, Date.now());
      genericStorage.set(STORAGE_KEY_DISMISS_COUNT, 1);
    } else if (currentDismissCount === 1) {
      // Second dismissal - increment count to 2, modal will never show again
      genericStorage.set(STORAGE_KEY_DISMISS_COUNT, 2);
    }
  };

  return {
    showModal,
    setShowModal,
    userEmail,
    hireButtonLoading,
    handleHolidayModalHireCamera,
    handleRemindLater,
    handleModalDismiss,
  };
}
